/**
 * MappingFieldsEditor — the rules of a field mapping.
 *
 * Each rule is one group: the output (normalised response field + optional
 * regex, with a tester) and, beside it from `md` up or under it on a phone,
 * its sources in the order they are tried.
 */
import React, { useState, useMemo } from 'react';
import { useTranslate } from 'react-admin';
import {
  Typography, IconButton, Button, TextField, Box, Autocomplete,
  Tooltip, Alert, Collapse, Stack, ToggleButtonGroup, ToggleButton, useTheme,
} from '@mui/material';
import {
  AddRounded as AddIcon, DeleteOutlineRounded as DeleteIcon, HelpOutlineRounded as HelpIcon,
  InfoOutlined as InfoIcon, ExpandMoreRounded as ExpandMore, ExpandLessRounded as ExpandLess,
  KeyboardArrowUpRounded as MoveUpIcon, KeyboardArrowDownRounded as MoveDownIcon,
  CloseRounded as RemoveRuleIcon,
} from '@mui/icons-material';
import { HelpDialog } from '@/components/shell';
import { RuleSummary, sourceName } from './RuleSummary';

const EDITOR = 'screens.fieldMappings.editor';

/* ── An explanation behind an info icon: tap or hover shows it, and it is the icon's name ── */
const InfoTip: React.FC<{ text: string }> = ({ text }) => (
  <Tooltip title={text} enterTouchDelay={0} leaveTouchDelay={8000}>
    <IconButton size="small" aria-label={text}>
      <InfoIcon fontSize="small" />
    </IconButton>
  </Tooltip>
);

/** A field label with its explanation. */
const LabelWithInfo: React.FC<{ label: string; info: string }> = ({ label, info }) => (
  <Stack direction="row" alignItems="center" spacing={0.25}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <InfoTip text={info} />
  </Stack>
);

/* ── Worked example, shown in the help dialog and on demand in the editor ── */
const EXAMPLE_SOURCES = [
  { field: 'MRZ_Surname', key: 'exampleMrz' },
  { field: 'VIZ_Surname', key: 'exampleViz' },
  { field: 'NORM_FRONT_ENG_Surname', key: 'exampleNorm' },
];

const ExampleBlock = () => {
  const t = useTranslate();
  const theme = useTheme();
  const mono = { fontFamily: theme.custom.text.mono };
  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2">{t(`${EDITOR}.exampleTitle`)}</Typography>
      <Typography variant="body2">
        <Box component="span" sx={mono}>surname</Box>
        <Box component="span" sx={{ color: 'text.secondary' }}> — {t(`${EDITOR}.exampleOutput`)}</Box>
      </Typography>
      <Typography variant="body2" color="text.secondary">{t(`${EDITOR}.exampleSources`)}</Typography>
      <Box component="ol" sx={{ m: 0, pl: 3 }}>
        {EXAMPLE_SOURCES.map((s) => (
          <Typography component="li" variant="body2" key={s.field}>
            <Box component="span" sx={mono}>{s.field}</Box>
            <Box component="span" sx={{ color: 'text.secondary' }}> ({t(`${EDITOR}.${s.key}`)})</Box>
          </Typography>
        ))}
      </Box>
      <Alert severity="info">{t(`${EDITOR}.exampleExplanation`)}</Alert>
    </Stack>
  );
};

/* ── Help: a ghost icon on the section heading, the explanation in a dialog ── */
export const MappingHelp = () => {
  const t = useTranslate();
  const [open, setOpen] = useState(false);
  const label = t(`${EDITOR}.helpTitle`);
  return (
    <>
      <Tooltip title={label}>
        <IconButton size="small" aria-label={label} onClick={() => setOpen(true)}>
          <HelpIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <HelpDialog open={open} onClose={() => setOpen(false)} title={label}>
        <Typography variant="body2">{t(`${EDITOR}.helpIntro`)}</Typography>
        <ExampleBlock />
      </HelpDialog>
    </>
  );
};

/* ── Regex tester ── */
const RegexCalculator = ({ regex }: { regex: string }) => {
  const t = useTranslate();
  const theme = useTheme();
  const [input, setInput] = useState('');

  const result = useMemo(() => {
    if (!regex || !input) return null;
    try {
      const match = input.match(new RegExp(regex));
      return { tone: match ? 'success' : 'warning', output: match ? match[0] : t(`${EDITOR}.regexNoMatch`) };
    } catch (e) {
      return { tone: 'error', output: t(`${EDITOR}.regexInvalid`, { message: e instanceof Error ? e.message : '' }) };
    }
  }, [regex, input, t]);

  if (!regex) return null;

  return (
    <Stack spacing={1}>
      <LabelWithInfo label={t(`${EDITOR}.regexTester`)} info={t(`${EDITOR}.regexTesterInfo`)} />
      <TextField
        placeholder={t(`${EDITOR}.regexTesterPlaceholder`)}
        size="small"
        fullWidth
        value={input}
        onChange={(e) => setInput(e.target.value)}
        inputProps={{ 'aria-label': t(`${EDITOR}.regexTester`) }}
      />
      {input && result && (
        <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
          <Box component="span" sx={{ color: 'text.secondary' }}>{t(`${EDITOR}.regexResult`)} </Box>
          <Box component="span" sx={{ fontFamily: theme.custom.text.mono, color: `${result.tone}.dark` }}>{result.output}</Box>
        </Typography>
      )}
    </Stack>
  );
};

/* ── Types ── */

interface KycMapping { prefix: string; kycField: { id: number; name: string }; suffix: string; _fixed?: boolean; }
interface MappingField { standardFieldName: string; regex: string; kycMappingField: KycMapping[]; }

interface KycFieldOption { id: number; name: string }
type StandardFieldOption = string | { fieldName?: string; name?: string; standardFieldName?: string };

interface MappingFieldsEditorProps {
  mappingFields: MappingField[];
  onChange: (fields: MappingField[]) => void;
  kycFields: KycFieldOption[];
  standardFields: StandardFieldOption[];
}

const getFieldLabel = (f: StandardFieldOption): string => {
  if (typeof f === 'string') return f;
  return f?.fieldName || f?.name || f?.standardFieldName || '';
};

const emptySource = (): KycMapping => ({ prefix: '', kycField: { id: 0, name: '' }, suffix: '' });

/** Explicit `_fixed` flag, or inferred from data (no KYC field but a prefix = a legacy fixed value). */
const sourceMode = (kyc: KycMapping): 'ocr' | 'fixed' => {
  if (kyc._fixed === true) return 'fixed';
  if (kyc._fixed === false) return 'ocr';
  return !kyc.kycField?.id && (kyc.kycField === null || kyc.prefix) ? 'fixed' : 'ocr';
};

/* ── Editor ── */

export const MappingFieldsEditor: React.FC<MappingFieldsEditorProps> = ({
  mappingFields, onChange, kycFields, standardFields,
}) => {
  const t = useTranslate();
  const theme = useTheme();
  const monoInput = { sx: { fontFamily: theme.custom.text.mono } };
  const [showExample, setShowExample] = useState(false);

  const update = (index: number, key: keyof MappingField, value: MappingField[keyof MappingField]) => {
    const updated = [...mappingFields];
    updated[index] = { ...updated[index], [key]: value };
    onChange(updated);
  };

  const addField = () => {
    onChange([...mappingFields, { standardFieldName: '', regex: '', kycMappingField: [emptySource()] }]);
  };

  const removeField = (index: number) => {
    if (mappingFields.length <= 1) return;
    onChange(mappingFields.filter((_, i) => i !== index));
  };

  const setSources = (fIdx: number, sources: KycMapping[]) => {
    const updated = [...mappingFields];
    updated[fIdx].kycMappingField = sources;
    onChange(updated);
  };

  const updateKyc = <K extends keyof KycMapping>(fIdx: number, kIdx: number, key: K, value: KycMapping[K]) => {
    setSources(fIdx, mappingFields[fIdx].kycMappingField.map((k, i) => (i === kIdx ? { ...k, [key]: value } : k)));
  };

  const addKyc = (fIdx: number) => setSources(fIdx, [...mappingFields[fIdx].kycMappingField, emptySource()]);

  const removeKyc = (fIdx: number, kIdx: number) => {
    if (mappingFields[fIdx].kycMappingField.length <= 1) return;
    setSources(fIdx, mappingFields[fIdx].kycMappingField.filter((_, i) => i !== kIdx));
  };

  const moveKyc = (fIdx: number, kIdx: number, direction: -1 | 1) => {
    const arr = [...mappingFields[fIdx].kycMappingField];
    const target = kIdx + direction;
    if (target < 0 || target >= arr.length) return;
    [arr[kIdx], arr[target]] = [arr[target], arr[kIdx]];
    setSources(fIdx, arr);
  };

  const changeMode = (fIdx: number, kIdx: number, newMode: 'ocr' | 'fixed' | null) => {
    if (!newMode) return;
    const src = { ...mappingFields[fIdx].kycMappingField[kIdx] };
    if (newMode === 'fixed') {
      src._fixed = true;
      src.kycField = { id: 0, name: '' };
    } else {
      src._fixed = false;
      src.prefix = '';
    }
    setSources(fIdx, mappingFields[fIdx].kycMappingField.map((k, i) => (i === kIdx ? src : k)));
  };

  const duplicates = new Set<string>();
  const seen = new Set<string>();
  mappingFields.forEach((f) => {
    const name = (f.standardFieldName || '').trim().toLowerCase();
    if (name && seen.has(name)) duplicates.add(name);
    if (name) seen.add(name);
  });

  const last = mappingFields[mappingFields.length - 1];
  const lastComplete = !last || ((last.standardFieldName || '').trim()
    && (last.kycMappingField || []).every((k) => k.kycField?.id || k.prefix));

  return (
    <Stack spacing={2}>
      {/* Count, example toggle and add */}
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
        <Typography variant="body2" color="text.secondary">
          {t(`${EDITOR}.ruleCount`, { smart_count: mappingFields.length })}
        </Typography>
        <Button
          variant="text"
          onClick={() => setShowExample(!showExample)}
          endIcon={showExample ? <ExpandLess /> : <ExpandMore />}
          aria-expanded={showExample}
        >
          {t(`${EDITOR}.showExample`)}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Tooltip title={t(lastComplete ? `${EDITOR}.addRuleHint` : `${EDITOR}.completeRuleFirst`)}>
          <span>
            <Button startIcon={<AddIcon />} onClick={addField} variant="outlined" disabled={!lastComplete}>
              {t(`${EDITOR}.addRule`)}
            </Button>
          </span>
        </Tooltip>
      </Stack>

      <Collapse in={showExample} unmountOnExit>
        <ExampleBlock />
      </Collapse>

      {duplicates.size > 0 && <Alert severity="warning">{t(`${EDITOR}.duplicates`)}</Alert>}

      {mappingFields.length === 0 && <Alert severity="info">{t(`${EDITOR}.noRules`)}</Alert>}

      {mappingFields.map((field, fIdx) => {
        const isDuplicate = duplicates.has((field.standardFieldName || '').trim().toLowerCase());
        const stdName = field.standardFieldName || '';
        const sources = field.kycMappingField || [];
        const allSourcesComplete = sources.every((k) => k.kycField?.id || (k.prefix && !k.kycField?.id));
        const removeRuleLabel = t(mappingFields.length <= 1 ? `${EDITOR}.oneRuleRequired` : `${EDITOR}.removeRule`);

        return (
          <Box
            key={fIdx}
            role="group"
            aria-label={t(`${EDITOR}.ruleNumber`, { n: fIdx + 1 })}
            sx={{
              border: 1,
              borderColor: isDuplicate ? 'warning.dark' : 'divider',
              borderRadius: theme.custom.radius.large,
            }}
          >
            {/* Rule heading: number, one-line summary, remove */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ px: 2, py: 0.5, borderBottom: 1, borderColor: 'divider', minWidth: 0 }}
            >
              <Typography variant="subtitle2" sx={{ flexShrink: 0 }}>{t(`${EDITOR}.ruleNumber`, { n: fIdx + 1 })}</Typography>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <RuleSummary output={stdName} sources={sources.map(sourceName).filter(Boolean)} regex={field.regex} />
              </Box>
              <Tooltip title={removeRuleLabel}>
                <span>
                  <IconButton onClick={() => removeField(fIdx)} disabled={mappingFields.length <= 1} aria-label={removeRuleLabel}>
                    <RemoveRuleIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
              {/* Output */}
              <Stack
                spacing={2}
                sx={{
                  width: { xs: '100%', md: '40%' },
                  p: 2,
                  borderRight: { md: 1 },
                  borderBottom: { xs: 1, md: 0 },
                  borderColor: 'divider',
                  minWidth: 0,
                }}
              >
                <LabelWithInfo label={t(`${EDITOR}.output`)} info={t(`${EDITOR}.outputInfo`)} />
                <Box>
                  <LabelWithInfo label={t(`${EDITOR}.normalisedField`)} info={t(`${EDITOR}.normalisedFieldInfo`)} />
                  <Autocomplete
                    freeSolo
                    options={standardFields}
                    getOptionLabel={getFieldLabel}
                    filterOptions={(options, { inputValue }) => {
                      const q = inputValue.toLowerCase();
                      return options.filter((o) => getFieldLabel(o).toLowerCase().includes(q));
                    }}
                    value={stdName}
                    onInputChange={(_, v) => update(fIdx, 'standardFieldName', v)}
                    renderOption={(props, option) => <li {...props} key={getFieldLabel(option)}>{getFieldLabel(option)}</li>}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={t(`${EDITOR}.normalisedFieldPlaceholder`)}
                        size="small"
                        required
                        error={isDuplicate}
                        helperText={isDuplicate ? t(`${EDITOR}.duplicateName`) : undefined}
                        inputProps={{ ...params.inputProps, 'aria-label': t(`${EDITOR}.normalisedField`) }}
                        InputProps={{ ...params.InputProps, ...monoInput }}
                      />
                    )}
                  />
                </Box>
                <Box>
                  <LabelWithInfo label={t(`${EDITOR}.regex`)} info={t(`${EDITOR}.regexInfo`)} />
                  <TextField
                    placeholder={t(`${EDITOR}.regexPlaceholder`)}
                    size="small"
                    value={field.regex || ''}
                    fullWidth
                    onChange={(e) => update(fIdx, 'regex', e.target.value)}
                    inputProps={{ 'aria-label': t(`${EDITOR}.regex`) }}
                    InputProps={monoInput}
                  />
                </Box>
                <RegexCalculator regex={field.regex || ''} />
              </Stack>

              {/* Sources */}
              <Stack spacing={1.5} sx={{ flex: 1, p: 2, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                  <LabelWithInfo label={t(`${EDITOR}.sources`)} info={t(`${EDITOR}.sourcesInfo`)} />
                  {sources.length > 1 && (
                    <Typography variant="body2" color="text.secondary">{t(`${EDITOR}.sourcesOrder`)}</Typography>
                  )}
                </Stack>

                {sources.map((kyc, kIdx) => {
                  const total = sources.length;
                  const mode = sourceMode(kyc);
                  const removeSourceLabel = t(total <= 1 ? `${EDITOR}.oneSourceRequired` : `${EDITOR}.removeSource`);

                  return (
                    <Stack
                      key={kIdx}
                      spacing={1}
                      sx={{ pt: kIdx > 0 ? 1.5 : 0, borderTop: kIdx > 0 ? 1 : 0, borderColor: 'divider' }}
                    >
                      {/* Order, kind of source, remove */}
                      <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap" useFlexGap>
                        <Typography variant="body2" sx={{ minWidth: 24 }} aria-label={t(`${EDITOR}.sourcePosition`, { n: kIdx + 1 })}>
                          {kIdx + 1}.
                        </Typography>
                        {total > 1 && (
                          <>
                            <Tooltip title={t('ra.action.move_up')}>
                              <span>
                                <IconButton onClick={() => moveKyc(fIdx, kIdx, -1)} disabled={kIdx === 0} aria-label={t('ra.action.move_up')}>
                                  <MoveUpIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={t('ra.action.move_down')}>
                              <span>
                                <IconButton onClick={() => moveKyc(fIdx, kIdx, 1)} disabled={kIdx === total - 1} aria-label={t('ra.action.move_down')}>
                                  <MoveDownIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </>
                        )}
                        <ToggleButtonGroup
                          value={mode}
                          exclusive
                          onChange={(_, v) => changeMode(fIdx, kIdx, v)}
                          size="small"
                          aria-label={t(`${EDITOR}.sourceKind`)}
                        >
                          <ToggleButton value="ocr">{t(`${EDITOR}.sourceOcr`)}</ToggleButton>
                          <ToggleButton value="fixed">{t(`${EDITOR}.sourceFixed`)}</ToggleButton>
                        </ToggleButtonGroup>
                        <Box sx={{ flex: 1 }} />
                        <Tooltip title={removeSourceLabel}>
                          <span>
                            <IconButton onClick={() => removeKyc(fIdx, kIdx)} disabled={total <= 1} aria-label={removeSourceLabel}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>

                      {mode === 'ocr' ? (
                        <Box>
                          <LabelWithInfo label={t(`${EDITOR}.ocrField`)} info={t(`${EDITOR}.ocrFieldInfo`)} />
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                            <TextField
                              label={t(`${EDITOR}.prefix`)}
                              helperText={t(`${EDITOR}.prefixHelp`)}
                              size="small"
                              value={kyc.prefix || ''}
                              onChange={(e) => updateKyc(fIdx, kIdx, 'prefix', e.target.value)}
                              InputProps={monoInput}
                              sx={{ width: { xs: '100%', sm: 112 }, flexShrink: 0 }}
                            />
                            <Autocomplete
                              sx={{ flex: 1, minWidth: 0 }}
                              options={kycFields}
                              getOptionLabel={(o) => o.name || ''}
                              value={kyc.kycField?.id ? kycFields.find((k) => k.id === kyc.kycField.id) || null : null}
                              onChange={(_, v) => updateKyc(fIdx, kIdx, 'kycField', v ? { id: v.id, name: v.name } : { id: 0, name: '' })}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={t(`${EDITOR}.ocrField`)}
                                  placeholder={t(`${EDITOR}.ocrFieldPlaceholder`)}
                                  size="small"
                                  error={!kyc.kycField?.id}
                                />
                              )}
                            />
                            <TextField
                              label={t(`${EDITOR}.suffix`)}
                              helperText={t(`${EDITOR}.suffixHelp`)}
                              size="small"
                              value={kyc.suffix || ''}
                              onChange={(e) => updateKyc(fIdx, kIdx, 'suffix', e.target.value)}
                              InputProps={monoInput}
                              sx={{ width: { xs: '100%', sm: 112 }, flexShrink: 0 }}
                            />
                          </Stack>
                        </Box>
                      ) : (
                        <Box>
                          <LabelWithInfo label={t(`${EDITOR}.fixedValue`)} info={t(`${EDITOR}.fixedValueInfo`)} />
                          <TextField
                            placeholder={t(`${EDITOR}.fixedValuePlaceholder`)}
                            size="small"
                            fullWidth
                            value={kyc.prefix || ''}
                            onChange={(e) => updateKyc(fIdx, kIdx, 'prefix', e.target.value)}
                            error={!kyc.prefix}
                            inputProps={{ 'aria-label': t(`${EDITOR}.fixedValue`) }}
                            InputProps={monoInput}
                          />
                        </Box>
                      )}
                    </Stack>
                  );
                })}

                <Tooltip title={t(allSourcesComplete ? `${EDITOR}.addSourceHint` : `${EDITOR}.completeSourcesFirst`)}>
                  <span>
                    <Button variant="text" onClick={() => addKyc(fIdx)} startIcon={<AddIcon />} disabled={!allSourcesComplete}>
                      {t(`${EDITOR}.addSource`)}
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
};
